// filepath: apps/web/src/auth/signinCallback.ts
//
// ISS-277 + ISS-377 NB-1 — the OIDC `onSigninCallback` handler.
//
// Extracted from `AuthProvider.tsx` into its own module so the
// path-only validation predicate (`isSafeSameOriginPath`) and the
// navigation side-effect (`handleSigninCallback`) can be unit-tested
// in isolation. The `AuthProvider` consumes this module's exports
// directly; the module boundary is internal to `apps/web/src/auth/`
// (the lower layer; `auth/` may not import from `features/`).
//
// Responsibilities:
//   1. ISS-277: clean the OIDC callback payload (?code&state) from
//      the URL after a successful code exchange (the library does
//      NOT navigate on its own — verified in the installed 3.5.0
//      bundle; the location.replace calls there belong to popup /
//      iframe child window navigators, not the main-window redirect
//      flow). Without this hook the URL stays frozen at
//      /oidc-callback?code&state and the SPA never advances.
//   2. ISS-377 NB-1: honour the persisted `returnTo` slot (set by
//      the apiFetch 401 → refresh-fail → redirectToLogin path) so
//      a failed-refresh login lands the user on the originating
//      page. The slot is consumed (read + cleared) inside this
//      handler.
//
// SECURITY:
//   - No tokens, no tenant identifiers flow through here — this is a
//     pure client-side routing signal.
//   - `isSafeSameOriginPath` rejects scheme-relative URLs (`//host`),
//     back-slash literals (Windows-pasted attacker URLs), and any
//     embedded scheme separator before the first path slash. The
//     predicate is enforced at CONSUMPTION time (not write time) so
//     a future consumer (e.g. the LoginRoute, if it adopts the same
//     write API) inherits the same harden policy.

import { readReturnTo, clearReturnTo } from "@/api";

/**
 * ISS-277: react-oidc-context's contract REQUIRES the consumer to
 * remove the OIDC payload (?code&state) from the URL after the
 * redirect back from the authorize endpoint. The library performs
 * no navigation or URL cleanup of its own.
 *
 * Mechanism — mirrors the SPA's established client-nav convention
 * (`features/cabinet/usePathname.ts`: history write + synthetic
 * `popstate` so every `usePathname()` consumer re-renders), with
 * two deliberate deviations:
 *
 *   1. `replaceState`, NOT `pushState`: the `code`/`state` params
 *      are one-time-use. A consumed callback URL left in the
 *      session history would re-trigger the callback on back /
 *      refresh and fail with an invalid_grant-style error.
 *   2. Implemented locally instead of reusing `navigate()` from
 *      `@/features/cabinet`: that helper uses `pushState` (wrong
 *      semantics here), and the auth module must not import from
 *      features/ (module-boundary rule — auth/ is a lower layer
 *      consumed by features/, never the reverse).
 *
 * The library awaits this hook AFTER a successful code exchange
 * and BEFORE dispatching the `INITIALISED` auth state — so the
 * brief window where `usePathname()` consumers re-render at the
 * resolved landing path while `auth.isLoading` is still true is
 * covered by AuthGuard's loading branch (no redirect loop).
 *
 * ISS-377 NB-1: when the persisted `returnTo` slot is non-empty
 * AND the value passes `isSafeSameOriginPath`, the user is landed
 * on the originating page instead of the default `/`. The slot is
 * always cleared (even on rejection) so a stale or hostile value
 * cannot survive past this callback.
 */
export function handleSigninCallback(): void {
  if (typeof window === "undefined") return;
  const persisted = readReturnTo();
  let landingPath = "/";
  if (typeof persisted === "string" && persisted.length > 0) {
    if (isSafeSameOriginPath(persisted)) {
      landingPath = persisted;
    }
    // Always clear — even on rejection — so a stale or hostile
    // value cannot survive past this callback.
    clearReturnTo();
  }
  // Land the user on the resolved path. The default
  // authenticated landing (cabinet home) is AuthStateRouter's
  // fall-through.
  //
  // ISS-377 round 2 NB-1: replaceState can throw a SecurityError
  // when the resolved URL contains a code-point path that the
  // history API rejects (e.g. a control character that slipped
  // past the predicate, or a future browser tightening on
  // printable ASCII paths). Without the try/catch the page is
  // left frozen at `/oidc-callback?code&state` — the user can
  // not navigate anywhere except via the OIDC signout flow. The
  // fallback is "/" plus a defensive clear of the returnTo slot.
  try {
    window.history.replaceState({}, document.title, landingPath);
  } catch {
    clearReturnTo();
    try {
      window.history.replaceState({}, document.title, "/");
    } catch {
      // Last-resort: window.location replace. If this fails the
      // browser is in an unrecoverable code path; surface the
      // throwable so the React error boundary catches it.
      if (typeof window !== "undefined") {
        window.location.replace("/");
      }
    }
  }
  // pushState/replaceState fire NO event — dispatch the synthetic
  // popstate manually so `usePathname()` subscribers (including
  // AuthStateRouter) re-render off the callback route.
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Open-redirect guard. The persisted `returnTo` is rejected unless
 * it is a same-origin path: must start with `/`, must NOT start
 * with `//` (scheme-relative URL — `//attacker.example/x` resolves
 * to a different origin), must NOT contain `\\`, must NOT contain
 * a scheme separator (`:`) within the FIRST path segment, and must
 * NOT contain any ASCII control character (`[\x00-\x1f]`). On
 * rejection the caller falls back to the default `/` — the
 * path is still cleared so the next callback does not retry the
 * same value.
 *
 * ISS-377 round 2 NB-1: the prior predicate accepted control
 * characters (`/\t/attacker`). After the predicate passes the
 * subsequent `history.replaceState(landingPath)` can throw a
 * SecurityError from inside `handleSigninCallback`, leaving the
 * page on `?code&state` — the user is stuck on the callback route.
 * The control-character reject at the predicate + the
 * try/catch around `replaceState` (see above) is the belt-and-braces
 * guarantee.
 *
 * Mirrors the conventional same-origin-path predicate used by
 * the OIDC ecosystem; the BA hardening note (uat-ba note_13124)
 * requires the check.
 *
 * Examples:
 *   - `/`                                       → accept
 *   - `/people/123`                             → accept
 *   - `/people/123?tab=employment`              → accept
 *   - `/foo/bar:baz`                            → accept (colon is past the first /)
 *   - `//attacker.example/x`                    → reject (scheme-relative URL)
 *   - `/\\attacker.example/x`                   → reject (back-slash literal)
 *   - `/javascript:alert(1)`                    → reject (scheme in first segment)
 *   - `/http://evil.com`                        → reject (absolute URL)
 *   - `people/123`                              → reject (does not start with /)
 *   - `/\t/attacker`                            → reject (control character)
 *   - `/\x07/evil`                              → reject (control character)
 */
export function isSafeSameOriginPath(value: string): boolean {
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  if (value.includes("\\")) return false;
  // Reject any ASCII control character (\x00–\x1f). These
  // characters sneak past the other checks but make
  // `history.replaceState(value)` throw a SecurityError in some
  // browser configurations. The BA hardening note requires this
  // reject at the predicate.
  if (/[\x00-\x1f]/.test(value)) return false;
  // Reject any `:` that appears in the FIRST path segment
  // (between the leading `/` and the next `/` or end of value).
  // `/javascript:alert(1)` has no second slash, so this still
  // catches it: the entire post-leading-`/` slice is the first
  // segment.
  const nextSlash = value.indexOf("/", 1);
  const firstSegmentEnd = nextSlash === -1 ? value.length : nextSlash;
  if (value.slice(1, firstSegmentEnd).includes(":")) return false;
  return true;
}
